/**
 * Executive Dashboard — PHOTON-80 (live-data slice).
 *
 * Renders /api/executive/summary (crm-service aggregate): four pillar cards
 * with per-source fresh/stale/failed states, the cached "Photon's Read"
 * commentary, and the severity-ranked action list. Layout and treatments are
 * 1:1 with docs/design/PHOTON-80/mockup.html; all figures, severities, and
 * display strings come from the server (single thresholds module lives
 * there), so this component is a pure renderer.
 *
 * All colors flow through CSS custom properties (spec §3) declared on the
 * root container — currently the light palette only, because the portal
 * shell hardcodes webLightTheme (dark theming is tracked as portal-level
 * work in the ticket).
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
} from "@fluentui/react-components";

import { toPillarData, useExecutiveSummary } from "./api";
import { ActionItem, DrillDown, PillarData } from "./types";
import { SEVERITY_CHIP_LABEL, SEVERITY_RANK, Severity } from "./severity";
import { Sparkline, SparkMode } from "./Sparkline";

/** Light-theme tokens (design-spec §3.2), applied as CSS custom properties. */
const LIGHT_TOKENS = {
  "--surface-page": "#EDF1F7",
  "--surface-card": "#FFFFFF",
  "--surface-sunken": "#F7F9FC",
  "--surface-hover": "#F2F6FB",
  "--stroke-default": "#DBE3EE",
  "--stroke-strong": "#C3CFDF",
  "--text-primary": "#0F1B2B",
  "--text-secondary": "#4A5A6E",
  "--text-muted": "#75859A",
  "--brand-primary": "#004C97",
  "--brand-deep": "#003A75",
  "--brand-text": "#0A5FB4",
  "--accent-ai": "#00C2D1",
  "--accent-ai-soft": "#E4F8FA",
  "--sev-ok": "#12855E",
  "--sev-ok-bg": "#E8F6EF",
  "--sev-warn": "#B96A00",
  "--sev-warn-bg": "#FDF3E2",
  "--sev-crit": "#C4314B",
  "--sev-crit-bg": "#FCEDEF",
  "--sev-info": "#2E7DD7",
  "--sev-info-bg": "#E9F2FC",
  "--shadow-rest": "0 1px 2px rgba(15,27,43,.06), 0 8px 24px rgba(15,27,43,.08)",
  "--shadow-lift": "0 2px 4px rgba(15,27,43,.08), 0 18px 40px rgba(15,27,43,.16)",
  "--radius-card": "14px",
  "--radius-control": "8px",
  "--radius-chip": "999px",
  "--font-display": "'Poppins','Segoe UI Variable Display','Segoe UI',system-ui,sans-serif",
  "--font-body": "'Open Sans','Segoe UI Variable Text','Segoe UI',system-ui,sans-serif",
} as React.CSSProperties;

const SEV_COLOR: Record<Severity, string> = {
  ok: "var(--sev-ok)",
  info: "var(--sev-info)",
  warn: "var(--sev-warn)",
  crit: "var(--sev-crit)",
};

const useStyles = makeStyles({
  root: {
    height: "100%",
    overflowY: "auto",
    backgroundColor: "var(--surface-page)",
    color: "var(--text-primary)",
    fontFamily: "var(--font-body)",
    fontSize: "14px",
    lineHeight: "1.5",
    padding: "24px 24px 60px",
  },
  wrap: {
    maxWidth: "1320px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  pagehead: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: "20px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "27px",
    fontWeight: 600,
    letterSpacing: "-0.4px",
    margin: 0,
  },
  pageSub: { color: "var(--text-secondary)", fontSize: "13px", marginTop: "3px" },
  periods: {
    display: "flex",
    gap: "6px",
    backgroundColor: "var(--surface-card)",
    padding: "4px",
    borderRadius: "10px",
    border: "1px solid var(--stroke-default)",
  },
  period: {
    padding: "6px 13px",
    borderRadius: "7px",
    color: "var(--text-secondary)",
    fontSize: "12.5px",
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    "&:hover": { backgroundColor: "var(--surface-hover)", color: "var(--text-primary)" },
  },
  periodOn: {
    backgroundColor: "var(--brand-primary)",
    color: "#fff",
    fontWeight: 600,
    "&:hover": { backgroundColor: "var(--brand-primary)", color: "#fff" },
  },

  deck: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "14px",
    "@media (max-width: 1149px)": { gridTemplateColumns: "repeat(2, 1fr)" },
    "@media (max-width: 767px)": { gridTemplateColumns: "1fr" },
  },
  pillar: {
    position: "relative",
    backgroundColor: "var(--surface-card)",
    border: "1px solid var(--stroke-default)",
    borderRadius: "var(--radius-card)",
    padding: "18px 18px 0",
    overflow: "hidden",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    boxShadow: "var(--shadow-rest)",
    fontFamily: "inherit",
    fontSize: "inherit",
    color: "inherit",
    transitionProperty: "transform, box-shadow",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "var(--shadow-lift)" },
    "&:focus-visible": { outline: "2px solid var(--accent-ai)", outlineOffset: "2px" },
    // Gradient edge stripe — severity color fades along the card height.
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      background: "linear-gradient(180deg, var(--sev) 0%, transparent 100%)",
    },
  },
  pillarStale: {
    border: "1px dashed var(--sev-warn)",
  },
  pillarFailed: {
    border: "1px dashed var(--sev-crit)",
    "::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      backgroundColor: "var(--sev-crit)",
      opacity: 0.045,
    },
  },
  pillarTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "10px",
    minHeight: "22px",
  },
  pillarLabel: {
    fontFamily: "var(--font-display)",
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: "1.1px",
    textTransform: "uppercase",
    color: "var(--text-muted)",
  },
  pillarValue: {
    fontFamily: "var(--font-display)",
    fontSize: "31px",
    fontWeight: 600,
    letterSpacing: "-1px",
    marginTop: "10px",
    lineHeight: 1.05,
    fontVariantNumeric: "tabular-nums",
  },
  pillarValueCrit: { color: "var(--sev-crit)" },
  pillarValueStale: { color: "var(--text-secondary)", opacity: 0.7 },
  pillarValueFailed: { color: "var(--text-muted)" },
  unit: {
    fontSize: "17px",
    fontWeight: 500,
    color: "var(--text-secondary)",
    letterSpacing: 0,
  },
  pillarMeta: {
    fontSize: "12.5px",
    color: "var(--text-secondary)",
    marginTop: "5px",
    minHeight: "34px",
  },
  lastKnown: {
    display: "block",
    fontSize: "11.5px",
    color: "var(--text-muted)",
    marginTop: "5px",
    lineHeight: 1.45,
  },
  lastKnownB: { fontWeight: 600, color: "var(--text-secondary)" },
  spark: { margin: "8px -18px 0", display: "block", width: "calc(100% + 36px)" },
  attrib: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    borderTop: "1px solid var(--stroke-default)",
    margin: "0 -18px",
    padding: "9px 18px",
    fontSize: "11px",
    color: "var(--text-muted)",
  },
  attribWarn: { color: "var(--sev-warn)" },
  attribCrit: { color: "var(--sev-crit)" },
  cta: {
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  },
  retry: {
    border: "1px solid var(--sev-crit)",
    color: "var(--sev-crit)",
    backgroundColor: "transparent",
    borderRadius: "6px",
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    "&:hover": { backgroundColor: "var(--sev-crit)", color: "var(--surface-card)" },
  },
  stateBar: {
    margin: "0 -18px",
    padding: "7px 18px",
    fontSize: "10.5px",
    letterSpacing: "0.2px",
    borderTop: "1px solid var(--stroke-default)",
  },
  stateBarWarn: { backgroundColor: "var(--sev-warn-bg)", color: "var(--sev-warn)" },
  stateBarCrit: { backgroundColor: "var(--sev-crit-bg)", color: "var(--sev-crit)" },

  chip: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.7px",
    textTransform: "uppercase",
    padding: "3px 8px",
    borderRadius: "var(--radius-chip)",
    whiteSpace: "nowrap",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    "::before": {
      content: '""',
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: "currentColor",
    },
  },
  chipGlyph: { "::before": { display: "none" } },
  chipOk: { color: "var(--sev-ok)", backgroundColor: "var(--sev-ok-bg)" },
  chipWarn: { color: "var(--sev-warn)", backgroundColor: "var(--sev-warn-bg)" },
  chipCrit: { color: "var(--sev-crit)", backgroundColor: "var(--sev-crit-bg)" },
  chipInfo: { color: "var(--sev-info)", backgroundColor: "var(--sev-info-bg)" },

  read: {
    position: "relative",
    backgroundColor: "var(--surface-card)",
    border: "1px solid var(--stroke-default)",
    borderRadius: "var(--radius-card)",
    padding: "20px 22px 20px 24px",
    boxShadow: "var(--shadow-rest)",
    overflow: "hidden",
    "::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: "4px",
      background: "linear-gradient(180deg, var(--accent-ai) 0%, var(--brand-text) 100%)",
    },
  },
  readHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "11px",
    flexWrap: "wrap",
  },
  readMark: {
    width: "26px",
    height: "26px",
    borderRadius: "7px",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    background: "linear-gradient(140deg, var(--brand-text), var(--accent-ai))",
    color: "#fff",
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: "13px",
    boxShadow: "0 0 14px rgba(0,194,209,.40)",
  },
  readTitle: { fontFamily: "var(--font-display)", fontSize: "15.5px", fontWeight: 600 },
  tag: {
    fontSize: "10px",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    fontWeight: 600,
    color: "var(--brand-text)",
    backgroundColor: "var(--sev-info-bg)",
    padding: "3px 8px",
    borderRadius: "5px",
  },
  readP: { margin: "0 0 12px", maxWidth: "78ch", fontSize: "14px", lineHeight: 1.62 },
  keyphrase: {
    fontWeight: 600,
    background: "linear-gradient(transparent 62%, rgba(0,194,209,.30) 62%)",
  },
  bullets: {
    listStyleType: "none",
    margin: 0,
    padding: 0,
    display: "grid",
    gap: "6px",
  },
  bullet: {
    display: "flex",
    gap: "9px",
    fontSize: "12.5px",
    color: "var(--text-secondary)",
    alignItems: "baseline",
    "::before": {
      content: '""',
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: "var(--accent-ai)",
      flexShrink: 0,
      transform: "translateY(-2px)",
    },
  },
  readFoot: {
    marginTop: "13px",
    paddingTop: "11px",
    borderTop: "1px solid var(--stroke-default)",
    fontSize: "11px",
    color: "var(--text-muted)",
  },

  panel: {
    backgroundColor: "var(--surface-card)",
    border: "1px solid var(--stroke-default)",
    borderRadius: "var(--radius-card)",
    boxShadow: "var(--shadow-rest)",
    overflow: "hidden",
  },
  panelHead: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    padding: "14px 18px",
    borderBottom: "1px solid var(--stroke-default)",
    backgroundColor: "var(--surface-sunken)",
  },
  panelTitle: {
    fontFamily: "var(--font-display)",
    fontSize: "15px",
    fontWeight: 600,
    flexGrow: 1,
    margin: 0,
  },
  panelGlyph: {
    width: "22px",
    height: "22px",
    borderRadius: "6px",
    display: "grid",
    placeItems: "center",
    backgroundColor: "var(--sev-crit-bg)",
    color: "var(--sev-crit)",
    fontSize: "12px",
    fontWeight: 700,
  },
  attRow: {
    display: "grid",
    gridTemplateColumns: "78px 1fr auto",
    alignItems: "center",
    gap: "14px",
    padding: "13px 18px",
    borderBottom: "1px solid var(--stroke-default)",
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    border: "none",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderBottomColor: "var(--stroke-default)",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    fontSize: "inherit",
    color: "inherit",
    transitionProperty: "background-color",
    transitionDuration: "140ms",
    "&:hover": { backgroundColor: "var(--surface-hover)" },
    "&:focus-visible": { outline: "2px solid var(--accent-ai)", outlineOffset: "-2px" },
  },
  attTitle: { display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "2px" },
  attDetail: { fontSize: "12.5px", color: "var(--text-secondary)" },
  attGo: { fontSize: "11.5px", color: "var(--text-muted)", whiteSpace: "nowrap" },

  drill: {
    borderTop: "1px solid var(--stroke-default)",
    backgroundColor: "var(--surface-sunken)",
  },
  drillInner: { padding: "16px 18px" },
  drillCaption: {
    fontFamily: "var(--font-display)",
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: "1.1px",
    textTransform: "uppercase",
    color: "var(--text-muted)",
    textAlign: "left",
    paddingBottom: "10px",
  },
  tblwrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "12.5px" },
  th: {
    textAlign: "left",
    fontFamily: "var(--font-display)",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.9px",
    textTransform: "uppercase",
    color: "var(--text-muted)",
    padding: "0 10px 8px",
    borderBottom: "1px solid var(--stroke-default)",
  },
  thRight: { textAlign: "right" },
  td: {
    padding: "9px 10px",
    borderBottom: "1px solid var(--stroke-default)",
    color: "var(--text-secondary)",
  },
  tdRight: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
  tdKey: { color: "var(--text-primary)", fontWeight: 500 },
  tdFlag: { color: "var(--sev-crit)" },
  linkbtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    marginTop: "13px",
    padding: "8px 14px",
    borderRadius: "var(--radius-control)",
    border: "1px solid var(--stroke-default)",
    backgroundColor: "var(--surface-card)",
    color: "var(--brand-text)",
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
    transitionProperty: "background-color, color, border-color",
    transitionDuration: "150ms",
    "&:hover": {
      backgroundColor: "var(--brand-primary)",
      color: "#fff",
      border: "1px solid var(--brand-primary)",
    },
    "&:focus-visible": { outline: "2px solid var(--accent-ai)", outlineOffset: "2px" },
  },
  drillNotice: {
    marginBottom: "12px",
    borderRadius: "6px",
    padding: "9px 12px",
    fontSize: "10.5px",
  },
  drillSrc: { marginTop: "11px", fontSize: "11px", color: "var(--text-muted)" },
  num: { fontVariantNumeric: "tabular-nums" },
});

const chipClassFor = (styles: ReturnType<typeof useStyles>, sev: Severity) =>
  sev === "ok"
    ? styles.chipOk
    : sev === "warn"
      ? styles.chipWarn
      : sev === "crit"
        ? styles.chipCrit
        : styles.chipInfo;

// ---------------------------------------------------------------------------
// Drill-down body (shared by pillar and action drills)
// ---------------------------------------------------------------------------
const DrillBody: React.FC<{ drill: DrillDown; onOpenTool: (id: string) => void }> = ({
  drill,
  onOpenTool,
}) => {
  const styles = useStyles();
  return (
    <div className={styles.drillInner}>
      {drill.notice && (
        <div
          className={mergeClasses(styles.stateBar, styles.stateBarCrit, styles.drillNotice)}
        >
          {drill.notice}
        </div>
      )}
      {drill.columns && drill.rows && (
        <div className={styles.tblwrap}>
          <table className={styles.table}>
            {drill.title && <caption className={styles.drillCaption}>{drill.title}</caption>}
            <thead>
              <tr>
                {drill.columns.map((c) => (
                  <th
                    key={c.label}
                    scope="col"
                    className={mergeClasses(styles.th, c.numeric && styles.thRight)}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {drill.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.cells.map((cell, ci) => (
                    <td
                      key={ci}
                      className={mergeClasses(
                        styles.td,
                        ci === 0 && styles.tdKey,
                        drill.columns![ci]?.numeric && styles.tdRight,
                        row.flagIndex === ci && styles.tdFlag,
                      )}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button className={styles.linkbtn} onClick={() => onOpenTool(drill.ctaToolId)}>
        {drill.ctaLabel}
      </button>
      {drill.source && <div className={styles.drillSrc}>{drill.source}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pillar card
// ---------------------------------------------------------------------------
const PillarCard: React.FC<{
  pillar: PillarData;
  expanded: boolean;
  onToggle: () => void;
  onRetry: () => void;
}> = ({ pillar, expanded, onToggle, onRetry }) => {
  const styles = useStyles();
  const state = pillar.state;
  const isStale = state.kind === "stale";
  const isFailed = state.kind === "failed";
  const sparkMode: SparkMode = isFailed ? "failed" : isStale ? "stale" : "healthy";

  return (
    <button
      className={mergeClasses(
        styles.pillar,
        isStale && styles.pillarStale,
        isFailed && styles.pillarFailed,
      )}
      style={{ "--sev": SEV_COLOR[pillar.severity] } as unknown as React.CSSProperties}
      aria-expanded={expanded}
      aria-controls={`drill-${pillar.id}`}
      onClick={onToggle}
    >
      <div className={styles.pillarTop}>
        <span className={styles.pillarLabel}>{pillar.label}</span>
        {isStale ? (
          <span className={mergeClasses(styles.chip, styles.chipWarn, styles.chipGlyph)}>
            <span aria-hidden="true">⏱</span> Stale
          </span>
        ) : isFailed ? (
          <span className={mergeClasses(styles.chip, styles.chipCrit, styles.chipGlyph)}>
            <span aria-hidden="true">⚠</span> Feed down
          </span>
        ) : (
          <span className={mergeClasses(styles.chip, chipClassFor(styles, pillar.severity))}>
            {SEVERITY_CHIP_LABEL[pillar.severity]}
          </span>
        )}
      </div>

      <div
        className={mergeClasses(
          styles.pillarValue,
          pillar.valueIsCritical && !isStale && !isFailed && styles.pillarValueCrit,
          isStale && styles.pillarValueStale,
          isFailed && styles.pillarValueFailed,
        )}
      >
        {isFailed ? "—" : pillar.value}
        {!isFailed && pillar.unit && <span className={styles.unit}> {pillar.unit}</span>}
      </div>

      <div className={styles.pillarMeta}>
        {isFailed && state.kind === "failed" ? (
          <span className={styles.lastKnown}>
            Last known: <b className={styles.lastKnownB}>{state.lastKnown.label}</b> (
            {state.lastKnown.asOfUtc}, {state.lastKnown.ageLabel})
          </span>
        ) : (
          pillar.context
        )}
      </div>

      <span className={styles.spark}>
        <Sparkline
          points={pillar.sparkY}
          color={SEV_COLOR[pillar.severity]}
          mode={sparkMode}
        />
      </span>

      <div
        className={mergeClasses(
          styles.attrib,
          isStale && styles.attribWarn,
          isFailed && styles.attribCrit,
        )}
      >
        {state.kind === "fresh" && (
          <span>
            {pillar.sourceName} · {state.asOfUtc}
          </span>
        )}
        {state.kind === "stale" && (
          <span>
            {pillar.sourceName} · stale — last good {state.lastGoodUtc}
          </span>
        )}
        {state.kind === "failed" && (
          <span>
            {pillar.sourceName} · {state.error}
          </span>
        )}
        {state.kind === "failed" ? (
          <span
            className={styles.retry}
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onRetry();
              }
            }}
          >
            Retry
          </span>
        ) : (
          <span className={styles.cta}>Detail →</span>
        )}
      </div>

      {state.kind === "stale" && (
        <div className={mergeClasses(styles.stateBar, styles.stateBarWarn)}>
          Data is {state.ageLabel} · {state.budgetLabel}. Figures may have moved.
        </div>
      )}
      {state.kind === "failed" && (
        <div className={mergeClasses(styles.stateBar, styles.stateBarCrit)}>
          Live figures unavailable. Do not treat the last known value as current.
        </div>
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export const ExecutiveDashboard: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [openPillar, setOpenPillar] = useState<string | null>(null);
  const [openAction, setOpenAction] = useState<string | null>(null);
  const { data, isLoading, isError, error, refetch, isFetching } = useExecutiveSummary();

  const openTool = (toolId: string) => navigate(`/tool/${toolId}`);

  const pillars: PillarData[] = useMemo(
    () => (data ? data.pillars.map(toPillarData) : []),
    [data],
  );
  const rankedActions = useMemo(
    () =>
      data
        ? [...data.actions].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
        : [],
    [data],
  );
  const openPillarData = pillars.find((pl) => pl.id === openPillar);
  const critCount = rankedActions.filter((a) => a.severity === "crit").length;
  const warnCount = rankedActions.filter((a) => a.severity === "warn").length;
  const narrative = data?.narrative ?? null;

  if (isLoading) {
    return (
      <div className={styles.root} style={LIGHT_TOKENS}>
        <div className={styles.wrap} style={{ alignItems: "center", paddingTop: "120px" }}>
          <Spinner size="large" label="Reading the business…" />
        </div>
      </div>
    );
  }

  if (isError || !data) {
    const message = error?.message ?? "Unknown error";
    const notEnabled = message.includes("404");
    const noAccess = message.includes("403");
    return (
      <div className={styles.root} style={LIGHT_TOKENS}>
        <div className={styles.wrap}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>
                {notEnabled
                  ? "Executive Dashboard is not enabled in this environment"
                  : noAccess
                    ? "You do not have access to the Executive Dashboard"
                    : "Could not load the executive summary"}
              </MessageBarTitle>
              {notEnabled || noAccess ? null : message}
            </MessageBarBody>
          </MessageBar>
          {!notEnabled && !noAccess && (
            <div>
              <Button appearance="primary" onClick={() => refetch()} disabled={isFetching}>
                {isFetching ? "Retrying…" : "Retry"}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root} style={LIGHT_TOKENS}>
      <div className={styles.wrap}>
        <div className={styles.pagehead}>
          <div>
            <h1 className={styles.pageTitle}>Executive Dashboard</h1>
            <div className={styles.pageSub}>
              LensLock Inc. · Fiscal YTD · Generated {data.generatedAtUtc}
              {isFetching ? " · refreshing…" : ""}
            </div>
          </div>
        </div>

        {/* 1. Status deck */}
        <section className={styles.deck} aria-label="Business health">
          {pillars.map((pillar) => (
            <PillarCard
              key={pillar.id}
              pillar={pillar}
              expanded={openPillar === pillar.id}
              onToggle={() =>
                setOpenPillar(openPillar === pillar.id ? null : pillar.id)
              }
              onRetry={() => refetch()}
            />
          ))}
        </section>

        {/* Pillar drill-down (in place beneath the deck, never a modal) */}
        {openPillarData?.drill && (
          <div className={styles.panel} id={`drill-${openPillarData.id}`}>
            <div className={styles.drill}>
              <DrillBody drill={openPillarData.drill} onOpenTool={openTool} />
            </div>
          </div>
        )}

        {/* 2. Photon's Read */}
        <section className={styles.read} aria-label="AI analysis">
          <div className={styles.readHead}>
            <div className={styles.readMark} aria-hidden="true">
              P
            </div>
            <span className={styles.readTitle}>
              {narrative ? narrative.title : "Photon's Read"}
            </span>
            <span className={styles.tag}>AI analysis</span>
          </div>
          {narrative ? (
            <>
              {narrative.paragraphs.map((para, i) => (
                <p key={i} className={styles.readP}>
                  {para.before}
                  {para.keyphrase && (
                    <span className={styles.keyphrase}>{para.keyphrase}</span>
                  )}
                  {para.after}
                </p>
              ))}
              <ul className={styles.bullets}>
                {narrative.bullets.map((b) => (
                  <li key={b} className={styles.bullet}>
                    {b}
                  </li>
                ))}
              </ul>
              <div className={styles.readFoot}>{narrative.footer}</div>
            </>
          ) : (
            <p className={styles.readP} style={{ color: "var(--text-muted)" }}>
              Commentary is unavailable this cycle. The figures above are live;
              the narrative will return on the next successful generation.
            </p>
          )}
        </section>

        {/* 3. Needs a decision */}
        <section className={styles.panel} aria-label="Needs a decision">
          <div className={styles.panelHead}>
            <div className={styles.panelGlyph} aria-hidden="true">
              !
            </div>
            <h2 className={styles.panelTitle}>Needs a decision</h2>
            <span className={mergeClasses(styles.chip, styles.chipCrit)}>
              {critCount} critical
            </span>
            <span className={mergeClasses(styles.chip, styles.chipWarn)}>
              {warnCount} watch
            </span>
          </div>
          {rankedActions.length === 0 && (
            <div className={styles.attRow} style={{ cursor: "default" }}>
              <span className={mergeClasses(styles.chip, styles.chipOk)}>
                {SEVERITY_CHIP_LABEL.ok}
              </span>
              <span className={styles.attDetail}>
                Nothing needs a decision right now.
              </span>
              <span />
            </div>
          )}
          {rankedActions.map((action: ActionItem) => (
            <React.Fragment key={action.id}>
              <button
                className={styles.attRow}
                aria-expanded={openAction === action.id}
                aria-controls={`drill-act-${action.id}`}
                onClick={() =>
                  setOpenAction(openAction === action.id ? null : action.id)
                }
              >
                <span className={mergeClasses(styles.chip, chipClassFor(styles, action.severity))}>
                  {SEVERITY_CHIP_LABEL[action.severity]}
                </span>
                <span>
                  <strong className={styles.attTitle}>{action.title}</strong>
                  <span className={styles.attDetail}>{action.detail}</span>
                </span>
                <span className={styles.attGo}>{action.goLabel}</span>
              </button>
              {openAction === action.id && (
                <div className={styles.drill} id={`drill-act-${action.id}`}>
                  <DrillBody drill={action.drill} onOpenTool={openTool} />
                </div>
              )}
            </React.Fragment>
          ))}
        </section>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
